import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'
import EastIcon from '@mui/icons-material/East'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  AlertColor,
  Badge,
  Box,
  Button,
  ButtonProps,
  CircularProgress,
  Grid,
  IconButton,
  List,
  Snackbar,
  Stack,
  Switch,
  Typography,
} from '@mui/material'
import { blue } from '@mui/material/colors'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import weekOfYear from 'dayjs/plugin/weekOfYear'
import { SyntheticEvent, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ShiftPayload } from '../../../types/interfaces'
import pathUrl from '../../api/pathURL'
import { useAuthContext } from '../../auth/hooks/useAuthContext'
import ScheduleDialog from '../../components/ScheduleDialog'
import useFetch from '../../hooks/use-fetch'
import useLocalStorage from '../../hooks/use-local-storage'
import { AvailableShiftCountType } from '../../types/interface'
import { getAvailableShifts } from '../../utils/schedule-actions'

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(weekOfYear)
dayjs.extend(relativeTime)

const days = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
]

type Message = {
  open: boolean
  severity: AlertColor
  text: string
}

const Shifts = () => {
  const { userStatus } = useAuthContext() // Initial load user data
  const { t } = useTranslation() // Translation function

  dayjs.tz.setDefault(userStatus?.tz) // Sets default user timezone

  const TODAY = {
    day: dayjs().tz().startOf('day'),
    week: dayjs().tz().startOf('week'),
  }

  const [date, setDate] = useState({
    day: dayjs().tz(),
    week: dayjs().tz().startOf('week'),
  })
  const isWeekNavigationDisabled =
    dayjs().tz().startOf('week').diff(date.week, 'week') >= 3 // Disable week navigation if the week is more than 3 weeks ago
  const isActionDisabled = date.day.isBefore(TODAY.day, 'day') // Disable action if the day is before today
  const [hourFormat, setHourFormat] = useLocalStorage('hour_format', 'hh:mm a')
  const [active, setActive] = useState<number>(dayjs().day())
  const [shiftCount, setShiftCount] = useState<AvailableShiftCountType[]>([])
  const { data, error, isLoading, refetch } = useFetch(
    `${pathUrl.getShifts}?day=${date.day.format('YYYY-MM-DD')}`
  ) // Fetch driver profile data

  const shiftsData = data as ShiftPayload

  const [message, setMessage] = useState<Message>({
    open: false,
    severity: 'success',
    text: '',
  })

  useEffect(() => {
    getAvailableShifts().then((res) => {
      setShiftCount(res)
    })
  }, [date])

  const onClicDaykHandler = (day: number) => {
    setActive(day)
    setDate({ ...date, day: date.week.day(day) })
  }

  const onClickWeekHandler = (direction: string) => {
    if (direction === 'next') {
      setDate({
        day: date.week.add(1, 'week').day(active),
        week: date.week.add(1, 'week'),
      })
    } else {
      setDate({
        day: date.week.subtract(1, 'week').day(active),
        week: date.week.subtract(1, 'week'),
      })
    }
  }

  const todayHandler = () => {
    setDate(TODAY)
    setActive(dayjs().tz().day())
  }

  const handleHourFormat = (state: boolean) => {
    const format = state ? 'HH:mm' : 'hh:mm a'

    setHourFormat(format)
  }

  const getShiftCount = (day: string) => {
    const shift = shiftCount.find((item) => item.day === day)
    if (shift?.has_shift) {
      return shift.count
    }
    return 0
  }

  const handleResponse = (values: Message) => {
    setMessage(values)
    refetch()
  }

  const handleClose = (event?: Event | SyntheticEvent, reason?: string) => {
    if (reason === 'clickaway') {
      return
    }

    setMessage({ ...message, open: false })
  }

  const gridContainerConfig = {
    alignItems: 'flex-start',
    container: true,
    justifyContent: 'center',
    sx: {
      rowGap: 1,
    },
  }

  const boxFlexConfig = {
    sx: {
      alignItems: 'center',
      display: 'flex',
      gap: 1,
      justifyContent: 'space-between',
      width: '100%',
    },
  }

  const gridItemFlexConfig = {
    item: true,
    sx: {
      alignItems: 'center',
      backgroundColor: '#FFF',
      columnGap: 0,
      display: 'flex',
      justifyContent: 'space-between',
      p: 2,
      width: '100%',
    },
  }

  const todayButtonConfig: ButtonProps = {
    disabled:
      date.day.format('DD-MM-YYYY') === dayjs().tz().format('DD-MM-YYYY'),
    onClick: todayHandler,
    variant: 'outlined',
  }

  if (isLoading) {
    return (
      <Grid {...gridContainerConfig}>
        <Grid
          alignItems="center"
          display="flex"
          item
          justifyContent="center"
          md={4}
          xs={12}
        >
          <CircularProgress />
        </Grid>
      </Grid>
    )
  }

  if (error) {
    return <div>Error</div>
  }

  return (
    <Grid
      container
      justifyContent="center"
      sx={{ backgroundColor: '#FAFAFA', height: '100vh', p: 2 }}
    >
      <Grid item md={4} xs={12}>
        <Grid {...gridContainerConfig}>
          <Grid {...gridItemFlexConfig}>
            <IconButton
              color="primary"
              disabled={isWeekNavigationDisabled}
              onClick={() => onClickWeekHandler('back')}
            >
              <ArrowBackIosIcon />
            </IconButton>
            <Box>
              <Typography align="center" variant="h6">
                {' '}
                {t('shift.week')} {date.week.week()}
              </Typography>
              <Box {...boxFlexConfig}>
                <Box>
                  <Typography
                    sx={{ fontWeight: 700, mr: '2px' }}
                    variant="overline"
                  >
                    {date.week.startOf('week').format('DD/MMM')}
                  </Typography>
                  <Typography variant="overline">
                    {date.week.startOf('week').format('YYYY')}
                  </Typography>
                </Box>
                <EastIcon />
                <Box>
                  <Typography
                    sx={{ fontWeight: 700, mr: '2px' }}
                    variant="overline"
                  >
                    {date.week.endOf('week').format('DD/MMM')}
                  </Typography>
                  <Typography variant="overline">
                    {date.week.endOf('week').format('YYYY')}
                  </Typography>
                </Box>
              </Box>
            </Box>
            <IconButton
              color="primary"
              disabled={
                date.week.week() ===
                dayjs(shiftCount.slice(-1)[0]?.day).tz().week()
              }
              onClick={() => onClickWeekHandler('next')}
            >
              <ArrowForwardIosIcon />
            </IconButton>
          </Grid>
          <Grid {...gridItemFlexConfig}>
            {days.map((day, idx) => (
              <Button
                color="secondary"
                disabled={
                  // Disables day buttons if last day of selected week is before last day of availble day
                  !date.week.day(idx).isBefore(
                    dayjs(shiftCount[shiftCount.length - 1]?.day)
                      .tz()
                      .add(1, 'day'),
                    'day'
                  )
                }
                key={day}
                onClick={() => onClicDaykHandler(idx)}
                size="small"
                sx={{ m: 0, minWidth: 48, py: 1 }}
                variant="text"
              >
                <Box>
                  <Typography
                    sx={{
                      ...(active === idx
                        ? { fontWeight: 800 }
                        : { fontWeight: 400 }),
                      display: 'block',
                      minWidth: 25,
                      mr: '2px',
                    }}
                    variant="overline"
                  >
                    {day.substring(0, 3)}
                  </Typography>
                  <Box>
                    <Badge
                      badgeContent={getShiftCount(
                        date.week.day(idx).format('YYYY-MM-DD')
                      )}
                      color="primary"
                      sx={{ width: '100%' }}
                    >
                      <Box
                        sx={{
                          width: '100%',
                          ...(active === idx
                            ? {
                                background: blue[500],
                                borderRadius: 1.5,
                                color: '#FFF',
                              }
                            : {}),
                        }}
                      >
                        <Typography
                          sx={{ fontWeight: 700, mr: '2px' }}
                          variant="overline"
                        >
                          {date.week.day(idx).format('DD')}
                        </Typography>
                      </Box>
                    </Badge>
                  </Box>
                </Box>
              </Button>
            ))}
          </Grid>
          <Grid {...gridItemFlexConfig}>
            <Button {...todayButtonConfig}>Today</Button>
            <Stack alignItems="center" direction="row" spacing={1}>
              <Typography variant="subtitle2">12h</Typography>
              <Switch
                checked={hourFormat === 'HH:mm'}
                onChange={(e) => handleHourFormat(e.target.checked)}
              />
              <Typography variant="subtitle2">24h</Typography>
            </Stack>
          </Grid>
          <Grid item xs={12}>
            {/* Pending List */}
            <Accordion
              disabled={!shiftsData.pending.length}
              elevation={0}
              expanded={shiftsData.pending.length > 0}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1">
                  {' '}
                  {t('shift.pending_shift')}{' '}
                </Typography>
                <Typography
                  sx={{ color: 'text.secondary', ml: 2 }}
                  variant="subtitle1"
                >
                  {shiftsData.pending.length}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0 }}>
                <List>
                  {shiftsData.pending.map((shift, idx) => (
                    <ScheduleDialog
                      data={shiftsData}
                      disableAction={isActionDisabled}
                      hourFormat={hourFormat}
                      key={shift.id || idx}
                      message={(values: Message) => handleResponse(values)}
                      shift={shift}
                      type="pending"
                    />
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>
            {/* Accepted List */}
            <Accordion disabled={!shiftsData.accepted.length} elevation={0}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1">
                  {' '}
                  {t('shift.accepted_shift')}{' '}
                </Typography>
                <Typography
                  sx={{ color: 'text.secondary', ml: 2 }}
                  variant="subtitle1"
                >
                  {shiftsData.accepted.length}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <List>
                  {shiftsData.accepted.map((shift, idx) => (
                    <ScheduleDialog
                      data={shiftsData}
                      disableAction={isActionDisabled}
                      hourFormat={hourFormat}
                      key={shift.id || idx}
                      message={(values: Message) => handleResponse(values)}
                      shift={shift}
                      type="accepted"
                    />
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>
            {/* Available List */}
            <Accordion disabled={!shiftsData.available.length} elevation={0}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1">
                  {' '}
                  {t('shift.available_shift')}{' '}
                </Typography>
                <Typography
                  sx={{ color: 'text.secondary', ml: 2 }}
                  variant="subtitle1"
                >
                  {shiftsData.available.length}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ mb: 6 }}>
                <List>
                  {shiftsData.available.map((shift, idx) => (
                    <ScheduleDialog
                      data={shiftsData}
                      disableAction={isActionDisabled}
                      hourFormat={hourFormat}
                      key={shift.id || idx}
                      message={(values: Message) => handleResponse(values)}
                      shift={shift}
                    />
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>

            <Snackbar
              autoHideDuration={6000}
              onClose={handleClose}
              open={message.open}
            >
              <Alert
                onClose={handleClose}
                severity={message.severity}
                sx={{ width: '100%' }}
              >
                {message.text}
              </Alert>
            </Snackbar>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  )
}

export default Shifts
